import { useEffect, useRef } from "react";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { purgeGroupReferences } from "./groupCleanup";

const LISTENER_ID = "dbm-group-sync";

type Options = {
  meUid?: string;
  /** Called when the logged-in user has just lost access to `guid`
   * (kicked/banned by someone else) — the caller clears its own
   * selection if it currently points at that group. */
  onRemoved: (guid: string, name: string) => void;
};

/**
 * App-wide group membership sync — the counterpart to useNotifications,
 * mounted once. Without this, being added to or removed from a channel by
 * someone ELSE (not your own join/leave action, which already refreshes
 * things locally) never reaches the sidebar or the open conversation until
 * a full page refresh.
 */
export function useGroupSync({ meUid, onRemoved }: Options) {
  const meUidRef = useRef(meUid);
  meUidRef.current = meUid;
  const onRemovedRef = useRef(onRemoved);
  onRemovedRef.current = onRemoved;

  useEffect(() => {
    const affectsMe = (uid?: string) => !!uid && uid === meUidRef.current;

    const onAdded = (uid?: string) => {
      // Being added to a channel/group by someone else — the sidebar's
      // own channel/group listeners only fire on new MESSAGES, not on
      // membership changes, so without this a fresh invite is invisible
      // until refresh.
      if (affectsMe(uid)) window.dispatchEvent(new Event("dbm-channels-changed"));
    };

    const onRemovedFromGroup = (uid: string | undefined, group: CometChat.Group) => {
      if (!affectsMe(uid)) return;
      purgeGroupReferences(group.getGuid());
      onRemovedRef.current(group.getGuid(), group.getName());
    };

    CometChat.addGroupListener(
      LISTENER_ID,
      new CometChat.GroupListener({
        onGroupMemberJoined: (_m: CometChat.BaseMessage, joined: CometChat.GroupMember) =>
          onAdded(joined?.getUid()),
        onMemberAddedToGroup: (_m: CometChat.BaseMessage, added: CometChat.User) => onAdded(added?.getUid()),
        onGroupMemberKicked: (
          _m: CometChat.BaseMessage,
          kicked: CometChat.User,
          _by: CometChat.User,
          from: CometChat.Group
        ) => onRemovedFromGroup(kicked?.getUid(), from),
        onGroupMemberBanned: (
          _m: CometChat.BaseMessage,
          banned: CometChat.User,
          _by: CometChat.User,
          from: CometChat.Group
        ) => onRemovedFromGroup(banned?.getUid(), from),
      })
    );
    return () => CometChat.removeGroupListener(LISTENER_ID);
  }, []);
}
